<template>
  <div class="container-fluid center">
    <portlet
        style="min-width:400px;max-width:480px;"
        :title="'Workfix portaal'"
        :actions-block="false"
    >
      <template slot="portletBody">
        <div v-if="stage === 'loading'" class="alert alert-info mb-0">Bezig met laden&hellip;</div>

        <div v-else-if="stage === 'expired'">
          <h5>Deze link is niet meer geldig</h5>
          <p class="text-muted">
            Een uitnodigingslink kan maar één keer gebruikt worden en verloopt na een tijdje.
            Vraag via "I forgot my password" op de inlogpagina een nieuw wachtwoord aan.
          </p>
          <a class="btn btn-primary" :href="portalOrigin">Naar de inlogpagina</a>
        </div>

        <div v-else-if="stage === 'error'">
          <h5>Er ging iets mis</h5>
          <p class="text-muted">{{ errorMessage }}</p>
          <a class="btn btn-primary" :href="invitationUrl">Opnieuw proberen</a>
        </div>

        <div v-else-if="stage === 'done'">
          <h5>Je wachtwoord is ingesteld</h5>
          <p class="text-muted">Je kunt nu inloggen met {{ email }}.</p>
          <a class="btn btn-primary" :href="portalOrigin">Naar de inlogpagina</a>
        </div>

        <form v-else @submit.prevent="submit">
          <p class="text-muted">
            Hoi {{ firstName }}, stel hieronder je eigen wachtwoord in. Je logt daarna in met
            <strong>{{ email }}</strong>.
          </p>

          <div class="form-group">
            <label for="invitation-password">Wachtwoord</label>
            <input
                id="invitation-password"
                type="password"
                class="form-control"
                autocomplete="new-password"
                v-model="password"
            />
          </div>

          <ul class="password-requirements">
            <li
                v-for="requirement in requirements"
                :key="requirement.key"
                :class="{met: requirement.isMet}"
            >
              <i class="fal" :class="requirement.isMet ? 'fa-check' : 'fa-times'"></i>
              {{ requirement.label }}
            </li>
          </ul>

          <div class="form-group">
            <label for="invitation-password-confirmation">Wachtwoord herhalen</label>
            <input
                id="invitation-password-confirmation"
                type="password"
                class="form-control"
                autocomplete="new-password"
                v-model="passwordConfirmation"
            />
            <span v-if="passwordConfirmation && !passwordsMatch" class="small text-danger">
              De wachtwoorden zijn niet gelijk.
            </span>
          </div>

          <span v-if="errorMessage" class="small text-danger d-block mb-2">{{ errorMessage }}</span>

          <button type="submit" class="btn btn-primary" :disabled="!canSubmit">
            Wachtwoord instellen
          </button>
        </form>
      </template>
    </portlet>
  </div>
</template>

<script>
import {describePasswordRequirements} from '@/utils/passwordRequirements';
import {describeInvitationOutcome, INVITATION_FAILED_FALLBACK} from '@/utils/clientUserInvitation';

export default {
  name: 'SetPassword',
  props: {
    token: String,
  },
  data() {
    return {
      stage: 'loading',
      submitting: false,
      email: '',
      firstName: '',
      password: '',
      passwordConfirmation: '',
      errorMessage: null,
    };
  },
  computed: {
    portalOrigin() {
      return window.location.origin;
    },
    invitationUrl() {
      return window.location.href;
    },
    requirements() {
      return describePasswordRequirements(this.password);
    },
    passwordsMatch() {
      return this.password === this.passwordConfirmation;
    },
    canSubmit() {
      return !this.submitting
        && this.passwordsMatch
        && this.requirements.every(requirement => requirement.isMet);
    },
  },
  methods: {
    async loadInvitation() {
      const outcome = describeInvitationOutcome(
        await this.$store.dispatch('clientUsers/fetchInvitation', {token: this.token}),
      );

      if (outcome.ok && outcome.data && outcome.data.email) {
        this.email = outcome.data.email;
        this.firstName = outcome.data.firstName;
        this.stage = 'form';

        return;
      }

      if (outcome.isNoLongerValid) {
        this.stage = 'expired';

        return;
      }

      this.stage = 'error';
      this.errorMessage = outcome.message || INVITATION_FAILED_FALLBACK;
    },
    async submit() {
      if (!this.canSubmit) {
        return;
      }

      this.submitting = true;
      this.errorMessage = null;

      const outcome = describeInvitationOutcome(
        await this.$store.dispatch('clientUsers/setInvitationPassword', {
          token: this.token,
          password: this.password,
          passwordConfirmation: this.passwordConfirmation,
        }),
      );

      this.submitting = false;

      if (outcome.ok) {
        this.stage = 'done';

        return;
      }

      if (outcome.isNoLongerValid) {
        this.stage = 'expired';

        return;
      }

      this.errorMessage = outcome.message;
    },
  },
  created() {
    this.loadInvitation();
  },
};
</script>

<style lang="scss" scoped>
@import "./../../assets/scss/_customFramework";

.center {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
}

.password-requirements {
  list-style: none;
  padding: 0;
  margin-bottom: 1.5rem;

  li {
    font-size: 13px;
    color: #8d97ad;

    &.met {
      color: $primary;
    }

    i {
      width: 16px;
      text-align: center;
    }
  }
}
</style>
